import type { AppData, Currency, Lot, SaleItem } from "./types";
import { convert, uid } from "./format";
import { lotRemaining } from "./metrics";

export interface ConsumptionResult {
  item: SaleItem | null;
  available: number;
  shortage: number;
}

/**
 * Consume lotes en orden FIFO para una venta.
 * El costo se expresa en la moneda de la venta para que la ganancia sea correcta.
 */
export function consumeLots(
  data: AppData,
  productId: string,
  productName: string,
  quantity: number,
  unitPrice: number,
  targetCurrency: Currency,
  rate: number,
): ConsumptionResult {
  const productLots = data.lots
    .filter((l) => l.productId === productId)
    .sort((a, b) => (a.purchaseDate < b.purchaseDate ? -1 : 1));

  const available = productLots.reduce(
    (sum, l) => sum + lotRemaining(l, data.sales, data.returns),
    0,
  );

  const consumptions: SaleItem["consumptions"] = [];
  let remaining = quantity;
  let costOfGoods = 0;

  for (const lot of productLots) {
    if (remaining <= 0) break;
    const lotQty = lotRemaining(lot, data.sales, data.returns);
    if (lotQty <= 0) continue;
    const take = Math.min(lotQty, remaining);
    const unitCost = convert(lot.unitCost, lot.currency, targetCurrency, rate);
    consumptions.push({ lotId: lot.id, quantity: take, unitCost });
    costOfGoods += take * unitCost;
    remaining -= take;
  }

  if (consumptions.length === 0) {
    return { item: null, available, shortage: quantity };
  }

  const item: SaleItem = {
    id: uid(),
    productId,
    productName,
    quantity: quantity - Math.max(0, remaining),
    unitPrice,
    costOfGoods,
    consumptions,
  };

  return { item, available, shortage: Math.max(0, remaining) };
}

export function lotForProduct(data: AppData, productId: string): Lot[] {
  return data.lots.filter((l) => l.productId === productId);
}

export function productRemaining(data: AppData, productId: string): number {
  return data.lots
    .filter((l) => l.productId === productId)
    .reduce((sum, l) => sum + lotRemaining(l, data.sales, data.returns), 0);
}